import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIsFetching } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import type { Control, UseFormRegister } from "react-hook-form";
import { useForm } from "react-hook-form";
import { jt, t } from "ttag";
import { z } from "zod";

import { appQueriesStore } from "@/app/api/queries";
import { useFillMissingInformation } from "@/app/hooks/use-fill-missing-information";
import { usePortalUserStore } from "@/app/stores";
import { useLongevityStore } from "@/features/longevity-onboarding/stores/use-longevity-store";
import { ImperialFields } from "@/features/onboarding/sections";
import { DateOfBirthField, GenderField, LogoLoader } from "@/shared/components";
import { PRODUCT_SLUG } from "@/shared/domains";
import {
  AGE_LIMIT,
  dateOfBirthSchema,
  imperialBmiInputsSchema,
  PHONE_NUMBERS,
  userAttributesSchema,
} from "@/shared/models";
import type { BmiForm, UserAttributes } from "@/shared/models";
import type { DateOfBirthValues } from "@/shared/models/legacy-types";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DocumentAddIcon,
  Form,
} from "@/shared/ui";
import { calculateAgeByDOB, formatDOB } from "@/shared/utils";

const AGE_ERROR_CODES = {
  MIN: "MIN_AGE_ERROR",
  MAX: "MAX_AGE_ERROR",
};

const buildMissingDobFormSchema = (minAge: number) =>
  z.object({
    dateOfBirth: dateOfBirthSchema.superRefine((dob, ctx) => {
      if (!dob.day || !dob.month || !dob.year) {
        return;
      }

      const age = calculateAgeByDOB(dob);

      if (age < minAge) {
        ctx.addIssue({
          code: "custom",
          message: AGE_ERROR_CODES.MIN,
          path: ["_age"],
        });
      }

      if (age > AGE_LIMIT.MAX) {
        ctx.addIssue({
          code: "custom",
          message: AGE_ERROR_CODES.MAX,
          path: ["_age"],
        });
      }
    }),
  });

const missingGenderFormSchema = z.object({
  gender: userAttributesSchema.shape.gender,
});

export const MissingDateOfBirthOrGenderModal = () => {
  const portalUser = usePortalUserStore((state) => state.portalUser);
  const longevityProduct = useLongevityStore((state) => state.productSlug);

  const isUserFetching = useIsFetching({
    queryKey: appQueriesStore.user.queryKey,
  });

  const missingDOB = !portalUser?.dateOfBirth;
  const missingGender = !portalUser?.gender;
  const missingHeight =
    portalUser?.heightFeet == null || portalUser?.heightInches == null;
  const missingWeight = !portalUser?.weightInPounds;
  const needsBmi =
    Boolean(longevityProduct) && (missingHeight || missingWeight);
  const show = Boolean(portalUser) && (missingDOB || missingGender);
  const minAge =
    longevityProduct === PRODUCT_SLUG.SERMORELIN
      ? AGE_LIMIT.LONGEVITY_MIN
      : AGE_LIMIT.MIN;

  const formSchema = useMemo(() => {
    const missingDobFormSchema = buildMissingDobFormSchema(minAge);

    if (missingDOB && missingGender) {
      const base = missingDobFormSchema.extend(missingGenderFormSchema.shape);
      return needsBmi ? base.extend(imperialBmiInputsSchema.shape) : base;
    }
    if (missingDOB) {
      return needsBmi
        ? missingDobFormSchema.extend(imperialBmiInputsSchema.shape)
        : missingDobFormSchema;
    }
    return needsBmi
      ? missingGenderFormSchema.extend(imperialBmiInputsSchema.shape)
      : missingGenderFormSchema;
  }, [missingDOB, missingGender, needsBmi, minAge]);

  type FormValues = z.infer<typeof formSchema>;

  const fillMissingInformation = useFillMissingInformation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const handleSubmit = form.handleSubmit((values) => {
    const dateOfBirth =
      "dateOfBirth" in values ? formatDOB(values.dateOfBirth) : undefined;
    const gender = "gender" in values ? values.gender : undefined;

    const physicalAttributes = (() => {
      if (!needsBmi || !("heightFeet" in values)) {
        return undefined;
      }

      const { heightFeet, heightInches, weightInPounds } =
        values as unknown as {
          heightFeet: number;
          heightInches: number;
          weightInPounds: number;
        };

      return { heightFeet, heightInches, weightInPounds };
    })();

    fillMissingInformation.mutate({
      dateOfBirth,
      gender,
      ...(physicalAttributes ?? {}),
    });
  });

  const {
    control,
    formState: { errors },
    register,
  } = form;

  const { dateOfBirth: dateOfBirthError } = errors as {
    dateOfBirth?: { _age?: { message?: string } };
  };

  const ageCode = dateOfBirthError?._age?.message;
  const isUnderMinAgeError = ageCode === AGE_ERROR_CODES.MIN;
  const isOverMaxAgeError = ageCode === AGE_ERROR_CODES.MAX;

  const ageMessage = isUnderMinAgeError
    ? t`${minAge} or older`
    : t`under 75 years old`;

  const contactSupport = (
    <a
      key="contact-support"
      href={PHONE_NUMBERS.CUSTOMER_SUPPORT.TEL_HREF}
      className="underline hover:no-underline"
    >
      {t`contact support`}
    </a>
  );

  return (
    <Dialog.Root open={show} alert>
      <Dialog.Content withCloseButton={false}>
        <Dialog.Header icon={DocumentAddIcon}>
          <Dialog.Title>{t`Something is missing...`}</Dialog.Title>
          <Dialog.Description>
            {t`Before we get started, we just need this missing info to help us personalize your experience.`}
          </Dialog.Description>
        </Dialog.Header>

        <Form.Provider {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
            {missingGender && (
              <GenderField
                control={
                  control as Control<{ gender: UserAttributes["gender"] }>
                }
              />
            )}
            {missingDOB && (
              <DateOfBirthField
                control={control as Control<{ dateOfBirth: DateOfBirthValues }>}
                register={
                  register as UseFormRegister<{
                    dateOfBirth: DateOfBirthValues;
                  }>
                }
              />
            )}
            {needsBmi && (
              <ImperialFields
                control={control as unknown as Control<BmiForm>}
              />
            )}

            <AnimatePresence>
              {(isUnderMinAgeError || isOverMaxAgeError) && (
                <Collapse key="age-alert">
                  <Alert type="error">
                    {jt`You must be ${ageMessage} to join LevelsRx. Please ${contactSupport} so we can help process your refund.`}
                  </Alert>
                </Collapse>
              )}
            </AnimatePresence>
            <Button type="submit" size="lg" className="self-center">
              {t`Save to profile`}
            </Button>
          </form>
        </Form.Provider>

        <LogoLoader
          show={fillMissingInformation.isPending || isUserFetching > 0}
          fullscreen
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};
